import i18n from '@/i18n/i18n'
import CommentService from '@/services/admin/CommentService.js'
import { errorMessage } from '@/utilities/helpers.js'

const { t } = i18n.global

export const namespaced = true

export const state = {
  orderComments: null,
  farmerComments: null,
  retailerComments: null,
  applicationComments: null,
  supplierComments: null
}

export const mutations = {
  SET_ORDER_COMMENTS(state, comments) {
    state.orderComments = comments.data
  },
  SET_FARMER_COMMENTS(state, comments) {
    state.farmerComments = comments.data
  },
  SET_RETAILER_COMMENTS(state, comments) {
    state.retailerComments = comments.data
  },
  SET_APPLICATION_COMMENTS(state, comments) {
    state.applicationComments = comments.data
  },
  SET_SUPPLIER_COMMENTS(state, comments) {
    state.supplierComments = comments.data
  },
  ADD_ORDER_COMMENT(state, comment) {
    if (!state.orderComments) {
      state.orderComments = []
    }
    state.orderComments.unshift(comment)
  },
  ADD_FARMER_COMMENT(state, comment) {
    state.farmerComments.unshift(comment)
  },
  ADD_RETAILER_COMMENT(state, comment) {
    state.retailerComments.unshift(comment)
  },
  ADD_APPLICATION_COMMENT(state, comment) {
    state.applicationComments.unshift(comment)
  },
  ADD_SUPPLIER_COMMENT(state, comment) {
    state.supplierComments.unshift(comment)
  },
  DELETE_ORDER_COMMENT(state, commentToRemove) {
    state.orderComments = state.orderComments.filter(comment => comment.id !== commentToRemove.id)
  },
  DELETE_FARMER_COMMENT(state, commentToRemove) {
    state.farmerComments = state.farmerComments.filter(comment => comment.id !== commentToRemove.id)
  },
  DELETE_RETAILER_COMMENT(state, commentToRemove) {
    state.retailerComments = state.retailerComments.filter(comment => comment.id !== commentToRemove.id)
  },
  DELETE_APPLICATION_COMMENT(state, commentToRemove) {
    state.applicationComments = state.applicationComments.filter(comment => comment.id !== commentToRemove.id)
  },
  DELETE_SUPPLIER_COMMENT(state, commentToRemove) {
    state.supplierComments = state.supplierComments.filter(comment => comment.id !== commentToRemove.id)
  }
}

export const actions = {
  getOrderComments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      CommentService.getOrderComments(id)
        .then(response => {
          commit('SET_ORDER_COMMENTS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.comment.comment')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createOrderComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.createOrderComment(params.orderId, params.params).then(response => {
        commit('ADD_ORDER_COMMENT', response.data)
        const notification = {
          type: 'success',
          message: t('store.shared.added', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  },
  deleteOrderComment({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      CommentService.deleteOrderComment(params.orderId, params.commentId)
        .then(response => {
          commit('DELETE_ORDER_COMMENT', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.comment')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFarmerComments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      CommentService.getFarmerComments(id)
        .then(response => {
          commit('SET_FARMER_COMMENTS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.comment.comments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createFarmerComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.createFarmerComment(params.farmerId, params.params).then(response => {
        commit('ADD_FARMER_COMMENT', response.data)
        const notification = {
          type: 'success',
          message: t('store.shared.added', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response)
      })
    })
  },
  deleteFarmerComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.deleteFarmerComment(params.farmerId, params.commentId).then(response => {
        commit('DELETE_FARMER_COMMENT', response.data)
        const notification = {
          type: 'warning',
          message: t('store.shared.deleted', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  },
  getRetailerComments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      CommentService.getRetailerComments(id)
        .then(response => {
          commit('SET_RETAILER_COMMENTS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.comment.comments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createRetailerComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.createRetailerComment(params.retailerId, params.params).then(response => {
        commit('ADD_RETAILER_COMMENT', response.data)
        const notification = {
          type: 'success',
          message: t('store.shared.added', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response)
      })
    })
  },
  deleteRetailerComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.deleteRetailerComment(params.retailerId, params.commentId).then(response => {
        commit('DELETE_RETAILER_COMMENT', response.data)
        const notification = {
          type: 'warning',
          message: t('store.shared.deleted', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  },
  getApplicationComments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      CommentService.getApplicationComments(id)
        .then(response => {
          commit('SET_APPLICATION_COMMENTS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.comment.comments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createApplicationComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.createApplicationComment(params.applicationId, params.params).then(response => {
        commit('ADD_APPLICATION_COMMENT', response.data)
        const notification = {
          type: 'success',
          message: t('store.shared.added', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response)
      })
    })
  },
  deleteApplicationComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.deleteApplicationComment(params.applicationId, params.commentId).then(response => {
        commit('DELETE_APPLICATION_COMMENT', response.data)
        const notification = {
          type: 'warning',
          message: t('store.shared.deleted', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  },
  getSupplierComments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      CommentService.getSupplierComments(id)
        .then(response => {
          commit('SET_SUPPLIER_COMMENTS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.comment.comment')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createSupplierComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.createSupplierComment(params.supplierId, params.params).then(response => {
        commit('ADD_SUPPLIER_COMMENT', response.data)
        const notification = {
          type: 'success',
          message: t('store.shared.added', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response)
      })
    })
  },
  deleteSupplierComment({ commit, dispatch }, params) {
    return new Promise(resolve => {
      CommentService.deleteSupplierComment(params.supplierId, params.commentId).then(response => {
        commit('DELETE_SUPPLIER_COMMENT', response.data)
        const notification = {
          type: 'warning',
          message: t('store.shared.deleted', {
            element: t('store.admin.comment.comment')
          })
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  }
}
